import React, { useEffect, useMemo, useCallback } from 'react';
import { Select, Alert, Spin, Form, message } from 'antd';
import { InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import InsuranceTable from 'components/PPAOnboarding/InsuranceTable';
import s from './s.module.less';
import useGetPPAServiceDetail from 'hooks/useGetPPAServiceDetail';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { EServiceType, InsuranceSetting, ServerKlarityService } from 'types/common';
import useGetPPAServicePriceConfig from 'hooks/useGetPPAServicePriceConfig';
import { INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE, INSURANCE_TYPE_INSURANCE } from 'constants/common';
import { IPPAServicePriceConfigResponse, EPPAStep, IPPAServiceDetailResponse } from 'types/ppaOnboarding';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';

interface StateOption {
    value: string;
    label: string;
}

interface ServiceFormValue {
    states: string[];
    serviceData?: ServerKlarityService;
}

interface ServiceFormProps {
    stateOptions: StateOption[];
    servicePriceConfig?: IPPAServicePriceConfigResponse[];
    paymentMethod?: typeof INSURANCE_TYPE_INSURANCE | typeof INSURANCE_TYPE_CASH | typeof INSURANCE_TYPE_CASH_AND_INSURANCE;
    selectedSpecialty?: string;
    value?: ServiceFormValue;
    onChange?: (value: ServiceFormValue) => void;
    initialInsuranceSettings?: Record<string, InsuranceSetting>;
}

const ServiceForm: React.FC<ServiceFormProps> = React.forwardRef(({
    stateOptions,
    servicePriceConfig,
    paymentMethod,
    selectedSpecialty,
    value,
    onChange,
    initialInsuranceSettings,
}, ref) => {
    const insuranceTableRef = React.useRef<{ getServerKlarityServiceData:() => ServerKlarityService }>();
    const [isEdit, setIsEdit] = React.useState(false);

    const handleStateChange = (states: string[]) => {
        try {
            setIsEdit(true);
            // 当州变化时，先更新 states，InsuranceTable 会在下一次渲染时更新数据
            onChange?.({
                states,
                // serviceData: insuranceTableRef.current?.getServerKlarityServiceData() || value?.serviceData,
                serviceData: value?.serviceData,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddInsurance = (stateCode: string) => {
        console.log('add insurance', stateCode);
    };

    // 监听 InsuranceTable 的数据变化
    // const handleInsuranceDataChange = useCallback(() => {
    //     // 使用 setTimeout 确保在下一个事件循环中获取最新数据
    //     setTimeout(() => {
    //         const serviceData = insuranceTableRef.current?.getServerKlarityServiceData();
    //         if (serviceData) {
    //             onChange?.({
    //                 states: value?.states || [],
    //                 serviceData,
    //             });
    //         }
    //     }, 0);
    // }, [onChange, value?.states]);

    React.useImperativeHandle(ref, () => ({
        getServerKlarityServiceData: () => {
            try {
                return insuranceTableRef.current?.getServerKlarityServiceData() || null;
            } catch (error) {
                console.error(error);
                return null;
            }
        },
    }));

    return (
        <>
            <div className={s.stateSelector}>
                <div className={s.selectorLabel}>Service states *</div>
                <div className={s.multiSelect}>
                    <Select
                        mode="multiple"
                        value={value?.states}
                        onChange={handleStateChange}
                        options={stateOptions}
                        className={s.stateSelect}
                        placeholder="Select states"
                        maxTagCount="responsive"
                        removeIcon={<CloseOutlined />}
                        suffixIcon={<span className={s.dropdownIcon}>▾</span>}
                    />
                </div>
            </div>

            <div className={s.tableSection}>
                <div className={s.tableHeader}>
                    <div className={s.tableTitle}>Appointment duration & payments *</div>
                    <div className={s.tableDescription}>
                        Configure visit duration, insurance coverage, and cash pay pricing.
                        Cash pay rates include suggestions and can be updated every 30 days.
                    </div>
                </div>
                <InsuranceTable
                    isEdit={isEdit}
                    ref={(ref) => {
                        if (ref) {
                            insuranceTableRef.current = ref;
                        }
                    }}
                    states={value?.states}
                    servicePriceConfig={servicePriceConfig}
                    className={s.insuranceTable}
                    onAddInsurance={handleAddInsurance}
                    paymentType={paymentMethod}
                    selectedSpecialty={selectedSpecialty}
                    initialInsuranceSettings={initialInsuranceSettings}
                    // onChange={() => setIsEdit(true)}
                />
            </div>
        </>
    );
});

interface IProps {
    formId?: string;
    onNext: (data: {
        providerKlarityService:ServerKlarityService[], disableIds:number[], conditions?:string[]
    }, step: EPPAStep) => void;
    serviceDetail?: IPPAServiceDetailResponse;
}

const Step4 = ({
    formId,
    onNext,
    serviceDetail,
}: IProps) => {
    const [form] = Form.useForm();
    const [savedServiceData, tableDataLoading] = useProviderKlarityServiceList();
    const [allStates] = useGetSettingData(EnumFields.STATE);
    const [servicePriceConfig, priceConfigLoading] = useGetPPAServicePriceConfig();
    const paymentMethod = serviceDetail?.supportPayType;
    const selectedSpecialty = serviceDetail?.selectedSpecialty;
    const tableData = useMemo(() => savedServiceData?.filter((item) => item.serviceType === EServiceType.INIT && item.serviceSpecialtyName === selectedSpecialty), [savedServiceData, selectedSpecialty]);
    const prefillState = useMemo(() => serviceDetail?.selectedState, [serviceDetail]);
    const serviceFormRef = React.useRef();

    const initialInsuranceSettings = useMemo(() => {
        if (!tableData) return {};
        return tableData.map((item) => item.state).reduce((acc: Record<string, InsuranceSetting>, state) => {
            const service = tableData.find((item) => item.state === state);
            if (!service) return acc;
            const carriers = Array.from(new Set(service.insurances?.map((item) => item.carrier)));
            acc[state] = {
                state,
                carriers: carriers.map((carrier) => ({
                    name: carrier,
                    plans: service.insurances?.filter((item) => item.carrier === carrier)?.map((item) => item.plan || ''),
                })),
                duration: service.duration,
                cashPrice: service.patientCost,
                cashPay: !!service.cashPay,
                insurancePay: !!service.insurancePay,
            };
            return acc;
        }, {});
    }, [tableData]);

    useEffect(() => {
        if (tableData && tableData.length > 0) {
            form.setFieldsValue({
                serviceForm: {
                    states: tableData.map((item) => item.state),
                },
            });
        } else if (prefillState && prefillState.length > 0) {
            form.setFieldsValue({
                serviceForm: { states: prefillState },
            });
        }
    }, [form, prefillState, tableData]);

    const stateOptions: StateOption[] = useMemo(() => {
        if (!allStates) return [];
        return allStates?.map((state) => ({
            value: state.dictKey,
            label: state.content,
        }));
    }, [allStates]);

    const handleFormFinish = async (values: { serviceForm: ServiceFormValue }) => {
        // 使用 Promise 和 setTimeout 确保获取到最新数据
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                // const formValue = form.getFieldValue('serviceForm');
                const serviceData = serviceFormRef.current?.getServerKlarityServiceData();
                if (serviceData) {
                    // 根据 states 和 serviceData 构建最终数据
                    const finalServiceData = {
                        ...serviceData,
                        serviceId: tableData?.[0]?.serviceId || null,
                        serviceStateItems: serviceData.serviceStateItems.filter(
                            (item) => values.serviceForm.states.includes(item.state),
                        ),
                        // 不需要在这里过滤 insurances，因为 InsuranceTable 已经根据 acceptInsurance 过滤过了
                        insurances: serviceData.insurances,
                    };

                    onNext({
                        providerKlarityService: [finalServiceData],
                        disableIds: [],
                        conditions: [],
                    }, EPPAStep.STEP_8);
                }
                resolve();
            }, 0);
        });
    };

    return (
        <div className={s.wrap}>
            <Spin spinning={priceConfigLoading || tableDataLoading}>
                <div className={s.contentContainer}>
                    <div className={s.header}>
                        <h1>Set up your initial visit details for {selectedSpecialty}</h1>
                        <p>Choose the states where you&apos;ll offer services and set your price.</p>
                    </div>

                    <Alert
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z" fill="#60A5FA" />
                            </svg>
                        }
                        description="Grow your potential patient reach by adding more states, carriers, and plans."
                        type="info"
                        showIcon
                        className={s.infoAlert}
                    />

                    <Form
                        form={form}
                        id={formId}
                        onFinish={handleFormFinish}
                        initialValues={{
                            serviceForm: {
                                states: [],
                                serviceData: null,
                            },
                        }}
                    >
                        <Form.Item
                            name="serviceForm"
                            noStyle
                            rules={[
                                {
                                    validator: async (_, value: ServiceFormValue) => {
                                        if (!value?.states || value.states.length === 0) {
                                            message.error('Please select at least one state');
                                            throw new Error('Please select at least one state');
                                        }
                                    // if (paymentMethod !== INSURANCE_TYPE_CASH && (!value?.serviceData || !value.states.every((state) => !!value.serviceData?.insurances.find((item) => item.state === state)))) {
                                    //     message.error('Please complete the insurance table settings');
                                    //     throw new Error('Please complete the insurance table settings');
                                    // }
                                    },
                                },
                            ]}
                        >
                            <ServiceForm
                                stateOptions={stateOptions}
                                servicePriceConfig={servicePriceConfig}
                                paymentMethod={paymentMethod}
                                selectedSpecialty={selectedSpecialty}
                                ref={serviceFormRef}
                                initialInsuranceSettings={initialInsuranceSettings}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </Spin>
        </div>
    );
};

export default Step4;
